import { getWPModuleSpacing } from "@app/api/wp-page-fetcher/utils/get-wp-module-spacing";
import { moduleMapperThrowMessage } from "@app/api/wp-page-fetcher/utils/module-mapper-error-handling";
import { IModuleFetcher } from "@app/api/wp-page-fetcher/module-selector/module-selector";
import Logger from "@app/util/logger";
import { HeaderHomepage2025Module } from "@app/api/modules/header-homepage/header-homepage-2025.module";
import { getAmountOfDealsApi } from "@app/middleware/api/total-amount-of-deals-get.api";
import { getAmountOfMerchantsApi } from "@app/middleware/api/total-amount-of-merchants-get.api";
import { bannersGetApi } from "@app/middleware/api/banners-get.api";
import { shuffleArray } from "@app/util/array";
import { FindAllByPlatformIdBannerTypeEnum } from "@app/bf-api";

import { IWPHeaderHomepage2025Module, IWPHeaderHomepage2025ModuleFull } from "./header-homepage-2025-module.interface";
import { WP_MODULES } from "../../module-names";
import { setMerchantsInPreloadedState } from "../merchant-overview-module";

export interface IHeaderHomepage2025ModuleMapper extends IModuleFetcher {
  module: IWPHeaderHomepage2025ModuleFull;
}

export const headerHomepage2025ModuleMapper = async (
  props: IHeaderHomepage2025ModuleMapper
): Promise<HeaderHomepage2025Module | undefined> => {
  try {
    const {
      module: { data, meta },
      platformId,
      res
    } = props;
    if (!data) return moduleMapperThrowMessage("No data found in headerHomepage2025ModuleMapper");

    if (res) {
      await setMerchantsInPreloadedState(platformId, res);
    }

    const banners = shuffleArray(
      (await bannersGetApi({ platformId, bannerType: FindAllByPlatformIdBannerTypeEnum.HERO_HEADER })) || []
    );

    return {
      id: WP_MODULES.HEADER_HOMEPAGE_2025.id,
      name: WP_MODULES.HEADER_HOMEPAGE_2025.wpName,
      ...getWPModuleSpacing(meta?.separators, meta?.setMargins),
      background: { backgroundColour: meta?.bgColor },
      ...data,
      buttons: await getButtons(data.buttons, platformId),
      banners
    };
  } catch (e) {
    Logger.logError(e, "Error in: headerHomepage2025ModuleMapper");

    return undefined;
  }
};

const getButtons = async (
  buttons: NonNullable<IWPHeaderHomepage2025Module>["buttons"],
  platformId: number
): Promise<HeaderHomepage2025Module["buttons"]> => {
  if (!buttons || !Array.isArray(buttons)) return [];
  const hasDealsCount = buttons.find((b) => b.counterType === "deals");
  const hasMerchantsCount = buttons.find((b) => b.counterType === "merchants");
  let dealsCount = 0;
  let merchantsCount = 0;

  if (hasDealsCount) {
    dealsCount = Number((await getAmountOfDealsApi(platformId)) || 0);
  }

  if (hasMerchantsCount) {
    merchantsCount = Number((await getAmountOfMerchantsApi(platformId)) || 0);
  }

  return buttons.map((button) => ({
    ...button,
    ...(button.counterType === "deals" && { count: dealsCount }),
    ...(button.counterType === "merchants" && { count: merchantsCount })
  }));
};
